import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "./config";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../S3StaticData/edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { useTranslation } from "react-i18next";

const filterS3TransactionsSchema = z.object({
  s3_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  s3_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  s3_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  s3_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  s3_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  s3_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

const filterS3TransactionsFields = filterS3TransactionsSchema.keyof().enum;

export type FilterS3TransactionsSchema = z.infer<
  typeof filterS3TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterS3TransactionsSchema>[] = [
  {
    name: filterS3TransactionsFields.s3_portfolio_bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterS3TransactionsFields.s3_portfolio_client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterS3TransactionsFields.s3_portfolio_vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterS3TransactionsFields.s3_portfolio_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterS3TransactionsFields.s3_portfolio_account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterS3Transactions() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.S3_TRANSACTIONS;

  const fields: Field[] = [
    {
      name: filterS3TransactionsFields.s3_portfolio_client_contains,
      label: t(`${tableName}.searchbox.s3_portfolio_client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterS3TransactionsSchema>(
        portfolioDepFields,
        filterS3TransactionsFields.s3_portfolio_client_contains,
      ),
    },
    {
      name: filterS3TransactionsFields.s3_portfolio_vehicle_contains,
      label: t(`${tableName}.searchbox.s3_portfolio_vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterS3TransactionsSchema>(
        portfolioDepFields,
        filterS3TransactionsFields.s3_portfolio_vehicle_contains,
      ),
    },
    {
      name: filterS3TransactionsFields.s3_portfolio_account_name_contains,
      label: t(`${tableName}.searchbox.s3_portfolio_account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterS3TransactionsSchema>(
        portfolioDepFields,
        filterS3TransactionsFields.s3_portfolio_account_name_contains,
      ),
    },
    {
      name: filterS3TransactionsFields.s3_portfolio_name_contains,
      label: t(`${tableName}.searchbox.s3_portfolio_name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterS3TransactionsSchema>(
        portfolioDepFields,
        filterS3TransactionsFields.s3_portfolio_name_contains,
      ),
    },
    {
      name: filterS3TransactionsFields.s3_portfolio_bank_name_contains,
      label: t(`${tableName}.searchbox.s3_portfolio_bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterS3TransactionsSchema>(
        portfolioDepFields,
        filterS3TransactionsFields.s3_portfolio_bank_name_contains,
      ),
    },
    {
      name: filterS3TransactionsFields.actif_contains,
      label: t(`${tableName}.searchbox.actif_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_STATIC_DATA}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterS3TransactionsFields.asset_class_1_equals,
      assetSubclassFieldName: filterS3TransactionsFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.S3_TRANSACTIONS}.searchbox.asset_class_1_equals`),
      assetSubclassLabel: t(`${Tables.S3_TRANSACTIONS}.searchbox.asset_class_2_equals`),
      client: Clients.S3,
      staticDataTableName: Tables.S3_STATIC_DATA,
      language: "fr",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterS3TransactionsFields.category_contains,
      label: t(`${tableName}.searchbox.category_contains`, { defaultValue: "Transaction type" }),
      type: "sectionSelect",
      useTranslation: true,
      section_name_translation_prefix: "transactions.category_groups",
      translate_key: "category",
      client: Clients.S3,
      table_name: Tables.S3_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_CATEGORY_OPTIONS,
    },
    {
      name: filterS3TransactionsFields.transfer_contains,
      label: t(`${tableName}.searchbox.transfer_contains`, { defaultValue: "Transfer type" }),
      type: "select",
      useTranslation: true,
      client: Clients.S3,
      table_name: Tables.S3_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: filterS3TransactionsFields.fee_type_contains,
      label: t(`${tableName}.searchbox.fee_type_contains`, { defaultValue: "Fee type" }),
      type: "select",
      useTranslation: true,
      client: Clients.S3,
      table_name: Tables.S3_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: filterS3TransactionsFields.description_contains,
      label: t(`${tableName}.searchbox.description_contains`, { defaultValue: "Description" }),
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterS3TransactionsFields.date_from },
      to: { name: filterS3TransactionsFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterS3TransactionsFields.create_from },
      to: { name: filterS3TransactionsFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.S3}
      schema={filterS3TransactionsSchema}
      tableName={Tables.S3_TRANSACTIONS}
      fields={fields}
    />
  );
}
